
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Plus, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Manufacturer } from '@/hooks/use-printer-search';

interface PrinterSuggestionFormProps {
  manufacturers: Manufacturer[];
  manufacturersLoading: boolean;
  onLoadManufacturers: (searchQuery?: string) => void;
  onSubmit: (printerName: string, manufacturerName: string) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const PrinterSuggestionForm: React.FC<PrinterSuggestionFormProps> = ({
  manufacturers,
  manufacturersLoading,
  onLoadManufacturers,
  onSubmit,
  onCancel,
  isSubmitting = false
}) => {
  const [printerName, setPrinterName] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [selectedManufacturerName, setSelectedManufacturerName] = useState('');
  const [newManufacturer, setNewManufacturer] = useState('');
  const [showNewManufacturer, setShowNewManufacturer] = useState(false);
  const [manufacturerDropdownOpen, setManufacturerDropdownOpen] = useState(false);
  const [manufacturerSearchQuery, setManufacturerSearchQuery] = useState('');
  const [errors, setErrors] = useState<{ printer?: string; manufacturer?: string }>({});
  const [hasLoadedInitialManufacturers, setHasLoadedInitialManufacturers] = useState(false);

  // Debug manufacturers prop
  useEffect(() => {
    console.log('🎯 PrinterSuggestionForm received manufacturers:', manufacturers);
    console.log('🎯 Manufacturers count:', manufacturers.length);
    console.log('🎯 Manufacturers loading:', manufacturersLoading);
  }, [manufacturers, manufacturersLoading]);

  // Load manufacturers when component mounts - but only once
  useEffect(() => {
    if (!hasLoadedInitialManufacturers && manufacturers.length === 0 && !manufacturersLoading) {
      console.log('🔄 Loading manufacturers on mount');
      onLoadManufacturers();
      setHasLoadedInitialManufacturers(true);
    }
  }, [manufacturers.length, manufacturersLoading, onLoadManufacturers, hasLoadedInitialManufacturers]);

  const handleManufacturerSearch = (query: string) => {
    console.log('🔍 Searching manufacturers with query:', query);
    setManufacturerSearchQuery(query);
    onLoadManufacturers(query);
  };

  const handleSelectManufacturer = (manufacturer: Manufacturer) => {
    console.log('✅ Selected manufacturer:', manufacturer);
    setSelectedManufacturer(manufacturer.id);
    setSelectedManufacturerName(manufacturer.name);
    setManufacturerDropdownOpen(false);
    setManufacturerSearchQuery('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: { printer?: string; manufacturer?: string } = {};
    
    if (!printerName.trim()) {
      newErrors.printer = 'Printer name is required';
    }
    
    const manufacturerToUse = showNewManufacturer ? newManufacturer : selectedManufacturerName;
    if (!manufacturerToUse.trim()) {
      newErrors.manufacturer = 'Manufacturer is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    try {
      await onSubmit(printerName.trim(), manufacturerToUse.trim());
      setPrinterName('');
      setSelectedManufacturer('');
      setSelectedManufacturerName('');
      setNewManufacturer('');
      setShowNewManufacturer(false);
      setErrors({});
    } catch (error) {
      console.error('Error submitting printer suggestion:', error);
    }
  };

  return (
    <Card className="border-dashed border-2 border-muted-foreground/25">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">Add New Printer</CardTitle>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="h-6 w-6 p-0"
          >
            <X size={12} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="printer-name" className="text-xs">
              Printer Name *
            </Label>
            <Input
              id="printer-name"
              value={printerName}
              onChange={(e) => setPrinterName(e.target.value)}
              placeholder="e.g., Ender 3 V2"
              className="h-8 text-sm"
              disabled={isSubmitting}
            />
            {errors.printer && (
              <p className="text-xs text-destructive">{errors.printer}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Manufacturer *</Label>
            <div className="space-y-2">
              {!showNewManufacturer ? (
                <div className="flex gap-2">
                  <Popover open={manufacturerDropdownOpen} onOpenChange={setManufacturerDropdownOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={manufacturerDropdownOpen}
                        className="flex-1 justify-between h-8 text-sm"
                        disabled={isSubmitting}
                      >
                        {selectedManufacturerName || (manufacturersLoading ? "Loading manufacturers..." : "Select manufacturer")}
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0" align="start">
                      <Command>
                        <CommandInput
                          placeholder="Search manufacturers..."
                          value={manufacturerSearchQuery}
                          onValueChange={handleManufacturerSearch}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {manufacturersLoading ? "Loading..." : "No manufacturers found that produce printers."}
                          </CommandEmpty>
                          <CommandGroup>
                            {manufacturers.map((manufacturer) => (
                              <CommandItem
                                key={manufacturer.id}
                                value={manufacturer.name}
                                onSelect={() => handleSelectManufacturer(manufacturer)}
                                className="cursor-pointer"
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedManufacturer === manufacturer.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {manufacturer.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowNewManufacturer(true)}
                    className="h-8 px-2"
                    disabled={isSubmitting}
                  >
                    <Plus size={12} />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={newManufacturer}
                    onChange={(e) => setNewManufacturer(e.target.value)}
                    placeholder="New manufacturer name"
                    className="h-8 text-sm"
                    disabled={isSubmitting}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowNewManufacturer(false);
                      setNewManufacturer('');
                    }}
                    className="h-8 px-2"
                    disabled={isSubmitting}
                  >
                    <X size={12} />
                  </Button>
                </div>
              )}
            </div>
            {errors.manufacturer && (
              <p className="text-xs text-destructive">{errors.manufacturer}</p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="submit"
              size="sm"
              className="h-8 text-xs"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Printer'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancel}
              className="h-8 text-xs"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default PrinterSuggestionForm;
